import { FC } from "react";
import { Box, Typography, TypographyProps } from "@mui/material";
import { gameState } from "@/services/gameState";
import { useTabManagement, WakeLockState } from "@context/TabManagementContext";
import { useAppDataContext } from "@context/AppContext";
import {
  BrowseGallery,
  ErrorOutlineOutlined,
  NetworkPing,
  NoEncryption,
  SvgIconComponent,
  Timeline,
  Update,
} from "@mui/icons-material";
import { TESTED_SAMS_SCORE_VERSION } from "@/services/gameZustandUpdaters";

type TextWithIcon = { label: SvgIconComponent; value: string; color?: string };

const AppVersion: FC<
  TypographyProps & { showAlwaysOnState: boolean; lines?: 1 | 2 }
> = ({ children, ref, showAlwaysOnState, lines = 1, ...props }) => {
  const lastAction = gameState("lastAction");
  const samsScoreVersion = gameState("samsScoreVersion");
  const latency = gameState("latency");
  const { wakeLockState } = useTabManagement();
  const { attemptAlwaysOnScreen } = useAppDataContext();

  const isSamsHigherVersion =
    [samsScoreVersion, TESTED_SAMS_SCORE_VERSION].sort().pop() ===
      samsScoreVersion && samsScoreVersion !== TESTED_SAMS_SCORE_VERSION;

  const texts: TextWithIcon[] = [
    { label: Update, value: import.meta.env.VITE_APP_VERSION || "" },
    ...(lastAction.orderRank >= 0
      ? [{ label: Timeline, value: lastAction.orderRank.toString() }]
      : []),
    ...(lastAction.receivedTimestamp
      ? [
          {
            label: BrowseGallery,
            value: formatTimeOffset(
              lastAction.receivedTimestamp - lastAction.timestamp,
            ),
          },
        ]
      : []),
    ...(latency
      ? [
          {
            label: NetworkPing,
            value: formatTimeOffset(latency, "~"),
            color:
              latency < 100
                ? "#19b319"
                : latency > 1000
                  ? "#ff0000"
                  : "#e1e131",
          },
        ]
      : []),
    ...(isSamsHigherVersion
      ? [
          {
            label: ErrorOutlineOutlined,
            value: `SAMS Version ungetestet (${samsScoreVersion})`,
            color: "#ff0000",
          },
        ]
      : []),
  ];

  const wakeStateTranslations: Record<WakeLockState, string> = {
    locked: "ON",
    requested: "REQ.",
    released: "OFF",
    unsupported: "N/A",
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent:
          attemptAlwaysOnScreen && showAlwaysOnState
            ? "space-between"
            : "flex-end",
        maxWidth: "100%",
        overflow: "hidden",
        gap: "2vw",
        flexShrink: 0,
        px: 1,
      }}
      {...props}
    >
      <Box
        display={"grid"}
        justifyItems={
          attemptAlwaysOnScreen && showAlwaysOnState ? "flex-start" : "flex-end"
        }
        maxWidth={"100%"}
        overflow={"hidden"}
        gridTemplateColumns={`repeat(${lines === 2 ? 2 : 4}, minmax(0, max-content))`}
      >
        {texts.map((text, index, array) => {
          return renderTextWithIcon(
            text,
            index.toString(),
            index < array.length - 1 && (lines === 1 || index % 2 === 0),
          );
        })}
      </Box>

      {attemptAlwaysOnScreen &&
        showAlwaysOnState &&
        renderTextWithIcon(
          {
            label: NoEncryption,
            value: wakeStateTranslations[wakeLockState],
          },
          "wake-lock-state",
          false,
        )}
    </Box>
  );
};

const renderTextWithIcon = (
  input: TextWithIcon,
  key: string,
  withDivider: boolean,
) => {
  const Icon = input.label;
  return (
    <Box flexShrink={0} display={"flex"} alignItems={"center"} key={key}>
      <Icon sx={{ fontSize: "0.9rem" }} />
      <Typography
        variant={"caption"}
        component={"p"}
        whiteSpace={"nowrap"}
        color={input.color ? input.color : "inherit"}
      >
        {`: ${input.value}`}
      </Typography>
      {withDivider && (
        <Typography variant={"caption"} component={"p"} px={"0.1rem"}>
          {`|`}
        </Typography>
      )}
    </Box>
  );
};

const formatTimeOffset = (offsetMs: number, prefix: string = "") => {
  if (offsetMs <= 1000) {
    return `${prefix}${offsetMs}ms`;
  } else if (offsetMs < 60 * 1000) {
    return `${prefix}${(offsetMs / 1000).toFixed(1)}s`;
  } else {
    return `${prefix}${(offsetMs / (60 * 1000)).toFixed(0)}m`;
  }
};

export default AppVersion;
